/**
 * Enhanced Wizard Payment Functionality
 * Handles payment processing and wizard enhancements
 */

export function initWizardPayment() {
  // Enhanced wizard payment functionality
  // This script monitors wizard creation and injects inline payment handling

  let wizardPaymentStripe = null;
  let wizardPaymentElements = null;
  let wizardPaymentCard = null;

  // Watch for wizard content changes and inject payment section
  function enhanceWizardWithPayment() {
      const wizardContent = document.getElementById('wizardContent');
      if (!wizardContent) return;

      // Create a MutationObserver to watch for wizard content changes
      const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
              if (mutation.type === 'childList' && wizardContent.innerHTML.length > 100) {
                  // Wizard was just populated - enhance it
                  setTimeout(() => {
                      injectPaymentSection();
                  }, 500);
              }
          }
      });

      observer.observe(wizardContent, { childList: true, subtree: true });
  }

  // Inject payment section into wizard - DISABLED (payment shown in charge summary instead)
  function injectPaymentSection() {
      // Payment information now shown in Charge Summary at bottom
      return;
  }

  // Update payment display based on selected customer
  function updateWizardPaymentDisplay() {
      const displayDiv = document.getElementById('wizardPaymentMethodDisplay');
      const newPaymentDiv = document.getElementById('wizardNewPaymentMethod');
      const detailsSpan = document.getElementById('wizardPaymentMethodDetails');

      if (!displayDiv || !newPaymentDiv) return;

      // Check if customer has saved payment method
      const customer = window.selectedCustomer || window.adminApp?.selectedCustomer;

      if (customer && customer.payment_methods && customer.payment_methods.length > 0) {
          // Customer has saved payment method
          const pm = customer.payment_methods[0];
          const brand = pm.card.brand.charAt(0).toUpperCase() + pm.card.brand.slice(1);
          const last4 = pm.card.last4;
          const expMonth = String(pm.card.exp_month).padStart(2, '0');
          const expYear = String(pm.card.exp_year).slice(-2);

          detailsSpan.textContent = `${brand} •••• ${last4} (exp ${expMonth}/${expYear})`;
          displayDiv.style.display = 'block';
          newPaymentDiv.style.display = 'none';

          // Store payment method ID for use in charge
          window.wizardSelectedPaymentMethodId = pm.id;

          console.log('✅ Using saved payment method:', pm.id);
      } else {
          // No saved payment method - show card entry
          displayDiv.style.display = 'none';
          newPaymentDiv.style.display = 'block';

          // Initialize Stripe Elements for card entry
          initializeWizardStripeElements();

          console.log('💳 No saved payment method - showing card entry');
      }
  }

  // Initialize Stripe Elements in wizard
  function initializeWizardStripeElements() {
      if (wizardPaymentCard) return; // Already initialized

      const cardElement = document.getElementById('wizard-card-element');
      if (!cardElement) return;

      const stripeKey = window.ENV?.VITE_STRIPE_PUBLISHABLE_KEY || import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY || '';

      if (!wizardPaymentStripe) {
          wizardPaymentStripe = Stripe(stripeKey);
      }

      if (!wizardPaymentElements) {
          wizardPaymentElements = wizardPaymentStripe.elements();
      }

      wizardPaymentCard = wizardPaymentElements.create('card', {
          style: {
              base: {
                  fontSize: '16px',
                  color: '#32325d',
                  fontFamily: '"Montserrat", "Helvetica Neue", Helvetica, sans-serif',
                  '::placeholder': {
                      color: '#aab7c4'
                  }
              },
              invalid: {
                  color: '#fa755a',
                  iconColor: '#fa755a'
              }
          }
      });

      wizardPaymentCard.mount('#wizard-card-element');

      wizardPaymentCard.on('change', (event) => {
          const errors = document.getElementById('wizard-card-errors');
          if (errors) {
              errors.textContent = event.error ? event.error.message : '';
          }
      });

      console.log('✅ Wizard Stripe Elements initialized');
  }

  // Enhanced charge customer function that uses wizard payment data
  window.chargeCustomerFromWizard = async function() {
      console.log('🚀 chargeCustomerFromWizard called');

      const customer = window.selectedCustomer || window.adminApp?.selectedCustomer;
      if (!customer) {
          alert('Please select a customer first');
          return false;
      }

      const serviceKey = window.currentServiceKey;
      if (!serviceKey) {
          alert('Please select a service first');
          return false;
      }

      // Get the charge amount
      const totalCostDisplay = document.getElementById('wizardTotalPrice') || document.getElementById('totalCostDisplay');
      const amount = totalCostDisplay ? parseFloat(totalCostDisplay.textContent.replace('$', '').replace(',', '')) : 0;

      if (amount <= 0) {
          alert('Invalid charge amount');
          return false;
      }

      console.log(`💰 Charging $${amount.toFixed(2)} to ${customer.name}`);

      // Check if we have a saved payment method or need to create one
      let paymentMethodId = window.wizardSelectedPaymentMethodId;

      if (!paymentMethodId && wizardPaymentCard) {
          // Create payment method from card input
          try {
              const { paymentMethod, error } = await wizardPaymentStripe.createPaymentMethod({
                  type: 'card',
                  card: wizardPaymentCard,
                  billing_details: {
                      name: customer.name,
                      email: customer.email,
                      phone: customer.phone
                  }
              });

              if (error) {
                  throw new Error(error.message);
              }

              paymentMethodId = paymentMethod.id;
              console.log('✅ Created new payment method:', paymentMethodId);
          } catch (error) {
              console.error('❌ Error creating payment method:', error);
              const errorDiv = document.getElementById('wizard-card-errors');
              if (errorDiv) {
                  errorDiv.textContent = error.message;
              }
              return false;
          }
      }

      if (!paymentMethodId) {
          alert('Payment method required');
          return false;
      }

      // Use the existing chargeCustomer flow but with wizard payment data
      if (window.adminApp && window.adminApp.chargeCustomer) {
          // Set the payment method ID for the charge
          window.wizardPaymentMethodIdForCharge = paymentMethodId;
          return await window.adminApp.chargeCustomer();
      } else {
          console.error('adminApp.chargeCustomer not available');
          return false;
      }
  };

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
      console.log('🎨 Wizard payment enhancement loaded');
      setTimeout(() => {
          enhanceWizardWithPayment();
      }, 1000);
  });

  // Wait for ALL scripts (including modules) to load before wrapping
  window.addEventListener('load', function() {
      console.log('🔧 Wrapping selectServiceDirect after page load...');

      // Store the current (module-loaded) version
      const originalSelectServiceDirect = window.selectServiceDirect;

      // Replace with our wrapper
      window.selectServiceDirect = function(serviceKey) {
          console.log('🚀 Wrapped selectServiceDirect called with:', serviceKey);

          // Call the original function
          if (originalSelectServiceDirect) {
              originalSelectServiceDirect(serviceKey);
          }

          // Wait for wizard fields to exist, then inject payment and auto-fill
          const waitForWizardFields = () => {
              let attempts = 0;
              const maxAttempts = 50; // 5 seconds max (50 * 100ms)

              const checkInterval = setInterval(() => {
                  attempts++;

                  // Check if wizard customer name field exists (primary indicator)
                  const nameField = document.getElementById('wizardCustomerName');

                  if (nameField) {
                      clearInterval(checkInterval);
                      console.log(`✅ Wizard fields ready after ${attempts * 100}ms`);

                      // Inject payment section
                      injectPaymentSection();

                      // Auto-fill customer data
                      if (window.autoFillWizardFromSelectedCustomer) {
                          window.autoFillWizardFromSelectedCustomer();
                      }
                  } else if (attempts >= maxAttempts) {
                      clearInterval(checkInterval);
                      console.warn('⚠️ Wizard fields not found after 5 seconds');
                  }
              }, 100); // Check every 100ms
          };

          waitForWizardFields();
      };

      console.log('✅ selectServiceDirect wrapper installed');
  });

  console.log('✅ Wizard payment functionality initialized');
}
